"use client";

import React from "react";
import { CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const marketingPoints = ["Professional high-resolution photography", "Syndication to 50+ premium rental sites", "Data-driven local market pricing analysis", "24/7 automated showing coordination"];
const screeningPoints = ["Nationwide criminal background checks", "Comprehensive credit history analysis", "Strict employment and income verification", "Thorough previous landlord references"];
export default function WhyKcInvestorsChooseUs() {
  const {
    ref: section1Ref,
    isVisible: isVisible1
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: section2Ref,
    isVisible: isVisible2
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="why-kc-investors-choose-us" className="bg-background py-[120px] overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {}
        <div ref={section1Ref} className={`grid md:grid-cols-2 gap-12 lg:gap-20 items-center transition-all duration-1000 ease-out ${isVisible1 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="relative group order-2 md:order-1">
            <div className="absolute -inset-4 bg-secondary/10 rounded-2xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
            <div className="relative rounded-xl overflow-hidden shadow-2xl border border-border/50 bg-card">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1584719/img/gladstone_neighborhood_homes.png" alt="Beautiful residential neighborhood in Gladstone, Kansas City" className="w-full h-[400px] md:h-[500px] object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent"></div>
            </div>
          </div>

          <div className="flex flex-col justify-center order-1 md:order-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 text-secondary text-sm font-semibold w-fit mb-6">
              <span className="w-2 h-2 rounded-full bg-secondary"></span>
              Maximized ROI
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-6">
              Aggressive Rental Property Marketing
            </h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              We don't just list your property; we actively market it across top
              channels to minimize vacancy and attract high-quality tenants
              throughout the Kansas City metro area.
            </p>

            <ul className="space-y-4 mb-10">
              {marketingPoints.map((point, i) => <li data-index={i} key={i} className="flex items-start gap-3">
                  <CircleCheck className="w-6 h-6 text-secondary shrink-0 mt-0.5" />
                  <span className="text-foreground font-medium">{point}</span>
                </li>)}
            </ul>

            <div>
              <Button asChild size="lg" className="shadow-sm">
                <Link to="https://clever-bluegill-1.10web.cloud/services#property-marketing">Explore Marketing Strategy</Link>
              </Button>
            </div>
          </div>
        </div>

        {}
        <div ref={section2Ref} className={`grid md:grid-cols-2 gap-12 lg:gap-20 items-center mt-24 lg:mt-32 transition-all duration-1000 ease-out delay-200 ${isVisible2 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="flex flex-col justify-center order-1">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 text-secondary text-sm font-semibold w-fit mb-6">
              <span className="w-2 h-2 rounded-full bg-secondary"></span>
              Risk Mitigation
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-6">
              Rigorous Tenant Screening Process
            </h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Protecting your investment starts with placing the right tenant.
              Our comprehensive screening process ensures reliable income,
              property care, and ultimate peace of mind for owners.
            </p>

            <ul className="space-y-4 mb-10">
              {screeningPoints.map((point, i) => <li data-index={i} key={i} className="flex items-start gap-3">
                  <CircleCheck className="w-6 h-6 text-secondary shrink-0 mt-0.5" />
                  <span className="text-foreground font-medium">{point}</span>
                </li>)}
            </ul>

            <div>
              <Button asChild size="lg" variant="outline" className="shadow-sm">
                <Link to="https://clever-bluegill-1.10web.cloud/services/">View All Services</Link>
              </Button>
            </div>
          </div>

          <div className="relative group order-2">
            <div className="absolute -inset-4 bg-secondary/10 rounded-2xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
            <div className="relative rounded-xl overflow-hidden shadow-2xl border border-border/50 bg-card">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1584719/img/tenant_screening_dashboard.png" alt="Professional tenant screening and background check interface" className="w-full h-[400px] md:h-[500px] object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent"></div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}